import React from 'react';

import { connect } from 'react-redux';
import { Button, Form } from 'antd';
import { Rule } from 'antd/lib/form';
import cx from 'classnames';
import Icon from '@ant-design/icons';

import { ReactComponent as CheckCircle } from '../../../../../assets/sightx-icons/svg/sx-check-circle.svg';
import { ReactComponent as AlertCircle } from '../../../../../assets/sightx-icons/svg/sx-alert-circle.svg';
import { SxInput } from '../../../../common/sxInput';
import { Features, isFeatureEnabled } from '../../../../../featureToggles/features';
import { RootState } from '../../../../../slices/rootReducer';
import { IPromotionalCodeDiscount } from '../../../../../slices/upgradeAccount/promotionalCode/interfaces';
import { verifyPromotionalCode } from '../../../../../slices/upgradeAccount/promotionalCode/thunk';
import { IVerifyPromotionalCodeData } from '../../../../../slices/upgradeAccount/promotionalCode/interfaces';
import { getIsValidatingCode, getPromotionalCode, getPromotionalDiscount, getValidationErrorMessage } from '../../../../../slices/upgradeAccount/promotionalCode/selectors';
import { BlockedByFeature } from '../../../../../featureToggles/features/utils';

import styles from './index.module.scss';

interface IMapStateToProps {
  isValidating: boolean;
  discount: IPromotionalCodeDiscount | undefined;
  errorMessage: string | undefined;
  promotionalCode: string | undefined;
}

interface IMapDispatchProps {
  verifyPromotionalCode: (params: IVerifyPromotionalCodeData) => void;
}

interface IPromotionalCodeProps {
  code: string | undefined;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onApply: () => void;
}

type IProps = IPromotionalCodeProps & IMapStateToProps & IMapDispatchProps;

class PromotionalCodeBase extends React.PureComponent<IProps> {
  constructor(props: IProps) {
    super(props);
    this.handleApply = this.handleApply.bind(this);
    this.validateField = this.validateField.bind(this);
  }

  componentDidUpdate(_prevProps: Readonly<IProps>): void {
    if (this.props.errorMessage) {
      this.props.onApply();
    }
  }

  private handleApply() {
    const { code, verifyPromotionalCode } = this.props;
    if (!code) {
      return;
    }
    verifyPromotionalCode({ code });
  }

  private renderValidationResult() {
    const { isValidating, discount, errorMessage } = this.props;
    let resultIcon = discount ? CheckCircle : undefined;
    if (errorMessage) {
      resultIcon = AlertCircle;
    }

    if (isValidating || !resultIcon) {
      return null;
    }
    return (
      <Icon className={cx(styles.icon, { [styles.success]: !!discount })} component={resultIcon} />
    );
  }

  private validateField (_rule: Rule, _value: string, callback: (msg?: string) => void) {
    const { errorMessage } = this.props;
    if (errorMessage) {
      callback(errorMessage);
    } else {
      callback();
    }
  }

  render() {
    if (!isFeatureEnabled(Features.PromotionalCodes)) {
      return null;
    }
    const { code, isValidating, promotionalCode, onChange } = this.props;
    return (
      <div className={styles.container}>
        <div className={styles.label}>Promotional code</div>
        <div className={styles.field}>
          <Form.Item name='promotionalCode' rules={[{ validator: this.validateField }]}>
            <div className={styles.inputContainer}>
              <SxInput
                placeholder='Code'
                value={code || promotionalCode}
                className={styles.code}
                onChange={onChange}
              />
              {this.renderValidationResult()}
            </div>
          </Form.Item>
          <Button
            type='default'
            disabled={!code || isValidating}
            className={styles.applyButton}
            onClick={this.handleApply}
          >
            Apply
          </Button>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  const isValidatingResult = getIsValidatingCode(state);
  const discountResult = getPromotionalDiscount(state);
  const errorMessageResult = getValidationErrorMessage(state);
  const promotionalCodeResult = getPromotionalCode(state);

  return {
    isValidating: isValidatingResult !== BlockedByFeature ? isValidatingResult : false,
    discount: discountResult !== BlockedByFeature ? discountResult : undefined,
    errorMessage: errorMessageResult !== BlockedByFeature ? errorMessageResult : undefined,
    promotionalCode: promotionalCodeResult !== BlockedByFeature ? promotionalCodeResult : undefined
  };
};

const mapDispatchToProps: IMapDispatchProps = {
  verifyPromotionalCode
};

export const PromotionalCode = connect(mapStateToProps, mapDispatchToProps)(PromotionalCodeBase);